import { Icon } from '@scality/core-ui';
import { OK_VITALS_THRESHOLD } from '../../../hooks/useVitalCardParams';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { NoData } from '../NoData';
import { MetricCardGrid } from '../shared/MetricCardGrid';
import { VitalCard, type VitalCardProps } from '../VitalCard/VitalCard';

type VitalsDashboardProps = {
  vitals: VitalCardProps[];
  timespanDays?: number;
};

/**
 * The vitals reading of a `MetricCardGrid`: the grid lays the cards out, this component writes the
 * one sentence that only makes sense for vitals — whether any score sits under the threshold.
 */
export const VitalsDashboard: AIToolComponent<VitalsDashboardProps> = ({ params }) => {
  if (params.vitals.length === 0) {
    return <NoData title="Vitals" />;
  }

  const lowestScore = Math.min(...params.vitals.map((param) => param.history[param.history.length - 1]));

  const header =
    lowestScore < OK_VITALS_THRESHOLD ? (
      <span>
        Some Vitals scores are below {OK_VITALS_THRESHOLD}. <Icon name="Times-circle" color="statusCritical" />
      </span>
    ) : (
      <span>
        All Vitals are above {lowestScore}. <Icon name="Check-circle" color="statusHealthy" />
      </span>
    );

  return (
    // TODO: add a dropdown to select the timespan, showing `params.timespanDays`
    <MetricCardGrid header={header}>
      {params.vitals.map((param) => (
        <VitalCard key={param.name} params={param} />
      ))}
    </MetricCardGrid>
  );
};

VitalsDashboard.description = 'Displays a dashboard of the overall platform health.';

VitalsDashboard.schema = {
  type: 'object',
  properties: {
    vitals: {
      type: 'array',
      items: VitalCard.schema,
      maxItems: 9,
    },
    timespanDays: {
      type: 'number',
      description: 'Number of days covered by the vitals data',
    },
  },
  required: ['vitals'],
};

VitalsDashboard.displayInPanel = true;
